'use client';

import {
  ConfidenceAccordion,
  TDocumentConfidenceLevel,
  TKycWithDocumentNamesRows,
} from '@verifime/components';
import { ReactElement } from 'react';
import KycWithNames from './KycWithNames';

export type TKycVerificationSectionProps = {
  /**
   * The rows to render in the documents table, one per identity document the customer holds.
   *
   * Built by the caller, not this component: turning a customer's documents into rows is a views
   * concern (ADR 50/62), and `lastUpdatedOn` and the confidence marking are already folded into
   * each row by the time it reaches here. See `KycWithNames` for the whole of that reasoning.
   */
  rows: TKycWithDocumentNamesRows;
  /**
   * The document confidence outcome, shown on the section's summary row.
   *
   * A prop, because this is a component and components do not fetch (ADR 50).
   *
   * Omit when there is no level. The summary row then carries the title alone.
   */
  level?: TDocumentConfidenceLevel;
  /**
   * Draws the summary row's meter and level name as placeholders.
   *
   * Separate from `isLoadingDocuments` because the two halves have different owners: the outcome
   * comes from the confidence endpoint and the documents come from the customer the page already
   * holds. An earlier version had one flag for both, on the reasoning that they resolve from one
   * request. They do not, and a single flag would either skeleton a table whose rows are already in
   * memory or show an outcome that has not arrived.
   */
  isLoadingOutcome?: boolean;
  /** Draws the table as placeholder rows while the customer is being fetched. */
  isLoadingDocuments?: boolean;
  'data-testid'?: string;
};

/**
 * The KYC/KYB section: a collapsible panel carrying the document outcome, over the documents table.
 *
 * **A component, not a view**, for the same reason as `KycWithNames`: it makes no API call, and per
 * ADR 62 that is what a View Component is for. It composes two components and takes everything else
 * as props, including the rows themselves rather than the customer's raw documents.
 *
 * **Open by default.** It is the page's primary content, and the prototype opens it.
 *
 * **The outcome is on the summary row, not above the table.** Collapsed, that row is the only thing
 * carrying it; expanded, it sits directly above the table, so restating it inside the body would be
 * saying the same thing twice a few pixels apart. Review asked for the restatement to go, not the
 * summary.
 *
 * **The two halves load independently**, which is why there are two flags rather than one. The
 * outcome comes from the confidence endpoint and the documents come from the customer object the
 * page already holds, so the table routinely has its rows while the outcome is still in flight.
 */
export default function KycVerificationSection({
  rows,
  level,
  isLoadingOutcome,
  isLoadingDocuments,
  'data-testid': dataTestId,
}: Readonly<TKycVerificationSectionProps>): ReactElement {
  return (
    <ConfidenceAccordion
      title="KYC/KYB"
      level={level}
      isLoading={isLoadingOutcome}
      defaultExpanded
      data-testid={dataTestId}
    >
      <KycWithNames rows={rows} isLoading={isLoadingDocuments} />
    </ConfidenceAccordion>
  );
}
